import React, { useState, useContext } from 'react';
import {getBaseUrl} from "../utils/apiUtils"
import { ResultsContext } from '../contexts/ResultsContext';
import { ToastContext } from '../contexts/ToastContext';
import './MatchingTournamentButton.css';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import {getTournamentTypeDisplay} from "../utils/constants"

/**
 * Button component to find and display matching tournament data (HD/4K)
 * 
 * @param {Object} props - Component props
 * @param {string} props.gameTitle - Title of the current game
 * @param {Array} props.currentLeaderboard - Leaderboard data for the current game
 * @param {Function} props.onMatchingDataFound - Callback when matching data is found
 */
function MatchingTournamentButton({ gameTitle, currentLeaderboard, onMatchingDataFound }) {
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  const [isLoading, setIsLoading] = useState(false);
  const { results, tournamentId } = useContext(ResultsContext);
  const { showToast } = useContext(ToastContext);
  
  // Get the current tournament type
  const currentType = results?.type || 'Unknown';
  
  // Determine the target type
  const targetType = currentType === 'atgames-4k' ? 'atgames-hd' : 'atgames-4k';
  
  // Get user-friendly type names
  const getUserFriendlyType = (type) => {
    return getTournamentTypeDisplay(type)
  };
  
  // Find matching tournament with the same date range
  const findMatchingTournament = async () => {
    setIsLoading(true);
    
    try {
      // Fetch the list of all tournaments
      const response = await fetch(`${getBaseUrl()}/at/tournaments`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tournaments: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success || !data.tournaments || data.tournaments.length === 0) {
        showToast('No tournaments found', 'error');
        return;
      }
      
      // Get current tournament data
      const currentTournament = data.tournaments.find(t => t.tournamentId === parseInt(tournamentId));
      if (!currentTournament || !currentTournament.start || !currentTournament.end) {
        showToast('Current tournament information not found', 'error');
        return;
      }
      
      // Find the matching tournament with opposite type and same date range
      const matchingTournament = data.tournaments.find(t => 
        t.type === targetType && 
        t.tournamentId !== parseInt(tournamentId) &&
        t.start === currentTournament.start &&
        t.end === currentTournament.end
      );
      
      if (!matchingTournament) {
        showToast(`No matching ${getUserFriendlyType(targetType)} tournament found with the same date range`, 'warning');
        return;
      }
      
      // Fetch data for the matching tournament
      const matchingResponse = await fetch(`${getBaseUrl()}${tournamentAppConfiguration.endPoints.results}${matchingTournament.tournamentId}`, {
        credentials: 'include'
      });
      
      if (!matchingResponse.ok) {
        throw new Error(`Failed to fetch matching tournament data: ${matchingResponse.status} ${matchingResponse.statusText}`);
      }
      
      const matchingData = await matchingResponse.json();
      
      if (!matchingData || !matchingData.games) {
        showToast('Failed to fetch data for the matching tournament', 'error');
        return;
      }
      
      // Find the matching game in the other tournament
      const matchingGame = matchingData.games.find(g => g.title === gameTitle);
      
      if (!matchingGame) {
        showToast(`Game "${gameTitle}" not found in the matching tournament`, 'warning');
        return;
      }
      
      // Call the callback with the consolidated data
      onMatchingDataFound({
        currentGame: {
          leaderboard: currentLeaderboard,
          type: currentType
        },
        matchingGame: {
          leaderboard: matchingGame.leaderboard,
          type: matchingData.type
        }
      });
      
    } catch (error) {
      console.error('Error finding matching tournament:', error);
      showToast(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Don't render the button if tournament type is unknown
  if (currentType !== 'atgames-4k' && currentType !== 'atgames-hd') {
    return null;
  }
  
  return (
    <button 
      className="matching-tournament-button"
      onClick={findMatchingTournament}
      disabled={isLoading}
    >
      {isLoading ? (
        <span className="loading-indicator">Loading...</span>
      ) : (
        `Find matching ${getUserFriendlyType(targetType)} tournament`
      )}
    </button>
  );
}

export default MatchingTournamentButton;